import { getSettings } from './Settings';

export class SoundSystem {
  private ctx: AudioContext;
  private masterGain: GainNode;
  private resumed = false;
  private duckCallback: (() => void) | null = null;

  constructor() {
    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);
    this.masterGain.gain.value = 0.4;

    const resume = (): void => {
      if (!this.resumed && this.ctx.state === 'suspended') {
        this.ctx.resume();
      }
      this.resumed = true;
      document.removeEventListener('pointerdown', resume);
      document.removeEventListener('keydown', resume);
    };
    document.addEventListener('pointerdown', resume);
    document.addEventListener('keydown', resume);
  }

  getContext(): AudioContext {
    return this.ctx;
  }

  getMasterGain(): GainNode {
    return this.masterGain;
  }

  setDuckCallback(cb: (() => void) | null): void {
    this.duckCallback = cb;
  }

  private duck(): void {
    this.duckCallback?.();
  }

  private isMuted(): boolean {
    return !getSettings().sfxEnabled;
  }

  playPlace(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Soft earthy "settle" — low sine with gentle attack, like a seed landing in soil
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(280, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + 0.12);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

    // Warm harmonic layer
    const harmonic = this.ctx.createOscillator();
    harmonic.type = 'sine';
    harmonic.frequency.setValueAtTime(560, now);
    harmonic.frequency.exponentialRampToValueAtTime(440, now + 0.08);

    const hGain = this.ctx.createGain();
    hGain.gain.setValueAtTime(0, now);
    hGain.gain.linearRampToValueAtTime(0.08, now + 0.01);
    hGain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);

    osc.connect(gain);
    gain.connect(this.masterGain);
    harmonic.connect(hGain);
    hGain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 0.15);
    harmonic.start(now);
    harmonic.stop(now + 0.1);
  }

  playInvalid(): void {
    if (this.isMuted()) return;
    const now = this.ctx.currentTime;

    // Soft woody "knock-knock" — two muted thumps like tapping stone
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(180, now);
    osc1.frequency.exponentialRampToValueAtTime(100, now + 0.06);

    const g1 = this.ctx.createGain();
    g1.gain.setValueAtTime(0.18, now);
    g1.gain.exponentialRampToValueAtTime(0.01, now + 0.06);

    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(160, now + 0.09);
    osc2.frequency.exponentialRampToValueAtTime(90, now + 0.15);

    const g2 = this.ctx.createGain();
    g2.gain.setValueAtTime(0.15, now + 0.09);
    g2.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc1.connect(g1);
    g1.connect(this.masterGain);
    osc2.connect(g2);
    g2.connect(this.masterGain);
    osc1.start(now);
    osc1.stop(now + 0.08);
    osc2.start(now + 0.09);
    osc2.stop(now + 0.18);
  }

  playDiscard(): void {
    if (this.isMuted()) return;
    const now = this.ctx.currentTime;

    // Breathy exhale — filtered noise sweep downward, like wind carrying a leaf away
    const bufferSize = Math.floor(this.ctx.sampleRate * 0.18);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      const env = Math.exp(-i / (bufferSize * 0.4));
      data[i] = (Math.random() * 2 - 1) * env;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.2;
    filter.frequency.setValueAtTime(1200, now);
    filter.frequency.exponentialRampToValueAtTime(300, now + 0.18);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.18);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    source.start(now);
    source.stop(now + 0.2);
  }

  playEruption(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Deep rumble — low oscillator with wobble, building then releasing
    const rumble = this.ctx.createOscillator();
    rumble.type = 'sine';
    rumble.frequency.setValueAtTime(40, now);
    rumble.frequency.linearRampToValueAtTime(60, now + 0.3);
    rumble.frequency.exponentialRampToValueAtTime(25, now + 0.8);

    const rumbleGain = this.ctx.createGain();
    rumbleGain.gain.setValueAtTime(0, now);
    rumbleGain.gain.linearRampToValueAtTime(0.35, now + 0.15);
    rumbleGain.gain.setValueAtTime(0.35, now + 0.4);
    rumbleGain.gain.exponentialRampToValueAtTime(0.01, now + 0.8);

    // LFO tremor on the rumble
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 8;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.15;
    lfo.connect(lfoGain);
    lfoGain.connect(rumbleGain.gain);

    rumble.connect(rumbleGain);
    rumbleGain.connect(this.masterGain);

    // Explosive burst — noise with resonant lowpass
    const burstLen = Math.floor(this.ctx.sampleRate * 0.5);
    const burstBuf = this.ctx.createBuffer(1, burstLen, this.ctx.sampleRate);
    const burstData = burstBuf.getChannelData(0);
    for (let i = 0; i < burstLen; i++) {
      burstData[i] = (Math.random() * 2 - 1);
    }
    const burstSrc = this.ctx.createBufferSource();
    burstSrc.buffer = burstBuf;

    const burstFilter = this.ctx.createBiquadFilter();
    burstFilter.type = 'lowpass';
    burstFilter.frequency.setValueAtTime(200, now);
    burstFilter.frequency.linearRampToValueAtTime(800, now + 0.1);
    burstFilter.frequency.exponentialRampToValueAtTime(100, now + 0.6);
    burstFilter.Q.value = 3;

    const burstGain = this.ctx.createGain();
    burstGain.gain.setValueAtTime(0, now);
    burstGain.gain.linearRampToValueAtTime(0.25, now + 0.08);
    burstGain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);

    burstSrc.connect(burstFilter);
    burstFilter.connect(burstGain);
    burstGain.connect(this.masterGain);

    // Crackle layer — highpass noise for sizzle
    const crackleLen = Math.floor(this.ctx.sampleRate * 0.4);
    const crackleBuf = this.ctx.createBuffer(1, crackleLen, this.ctx.sampleRate);
    const crackleData = crackleBuf.getChannelData(0);
    for (let i = 0; i < crackleLen; i++) {
      crackleData[i] = (Math.random() * 2 - 1) * (Math.random() > 0.7 ? 1 : 0.2);
    }
    const crackleSrc = this.ctx.createBufferSource();
    crackleSrc.buffer = crackleBuf;

    const crackleFilter = this.ctx.createBiquadFilter();
    crackleFilter.type = 'highpass';
    crackleFilter.frequency.value = 2000;

    const crackleGain = this.ctx.createGain();
    crackleGain.gain.setValueAtTime(0, now + 0.1);
    crackleGain.gain.linearRampToValueAtTime(0.12, now + 0.2);
    crackleGain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);

    crackleSrc.connect(crackleFilter);
    crackleFilter.connect(crackleGain);
    crackleGain.connect(this.masterGain);

    rumble.start(now);
    rumble.stop(now + 0.85);
    lfo.start(now);
    lfo.stop(now + 0.85);
    burstSrc.start(now);
    burstSrc.stop(now + 0.65);
    crackleSrc.start(now + 0.1);
    crackleSrc.stop(now + 0.65);
  }

  playClear(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Wind chime cascade — pentatonic notes with gentle attack, like rain on leaves
    const notes = [392, 523, 587, 698];
    notes.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const gain = this.ctx.createGain();
      const start = now + i * 0.07;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.18 - i * 0.02, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.25);

      // Soft shimmer from detuned pair
      const detune = this.ctx.createOscillator();
      detune.type = 'sine';
      detune.frequency.value = freq * 1.003;

      const dGain = this.ctx.createGain();
      dGain.gain.setValueAtTime(0, start);
      dGain.gain.linearRampToValueAtTime(0.06, start + 0.02);
      dGain.gain.exponentialRampToValueAtTime(0.01, start + 0.2);

      osc.connect(gain);
      gain.connect(this.masterGain);
      detune.connect(dGain);
      dGain.connect(this.masterGain);

      osc.start(start);
      osc.stop(start + 0.3);
      detune.start(start);
      detune.stop(start + 0.25);
    });
  }

  playBlip(): void {
    if (this.isMuted()) return;
    const now = this.ctx.currentTime;

    // Water droplet — sine with quick pitch bend down, like a single raindrop
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1400, now);
    osc.frequency.exponentialRampToValueAtTime(600, now + 0.04);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.06);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 0.08);
  }

  playSpiritPower(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Magical shimmer — ascending pentatonic with layered harmonics, like forest bells
    const notes = [587, 698, 880, 1047, 1175];
    notes.forEach((freq, i) => {
      const start = now + i * 0.055;

      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.14 - i * 0.015, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.2);

      // Triangle harmonic for sparkle
      const tri = this.ctx.createOscillator();
      tri.type = 'triangle';
      tri.frequency.value = freq * 2.01;

      const tGain = this.ctx.createGain();
      tGain.gain.setValueAtTime(0, start);
      tGain.gain.linearRampToValueAtTime(0.04, start + 0.01);
      tGain.gain.exponentialRampToValueAtTime(0.01, start + 0.15);

      osc.connect(gain);
      gain.connect(this.masterGain);
      tri.connect(tGain);
      tGain.connect(this.masterGain);

      osc.start(start);
      osc.stop(start + 0.25);
      tri.start(start);
      tri.stop(start + 0.2);
    });
  }

  playShieldEarn(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Warm protective resonance — mid-frequency tone with soft overtone bloom
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(330, now);
    osc.frequency.linearRampToValueAtTime(392, now + 0.15);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.03);
    gain.gain.setValueAtTime(0.2, now + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);

    const overtone = this.ctx.createOscillator();
    overtone.type = 'sine';
    overtone.frequency.setValueAtTime(660, now);
    overtone.frequency.linearRampToValueAtTime(784, now + 0.15);

    const oGain = this.ctx.createGain();
    oGain.gain.setValueAtTime(0, now + 0.05);
    oGain.gain.linearRampToValueAtTime(0.1, now + 0.08);
    oGain.gain.exponentialRampToValueAtTime(0.01, now + 0.22);

    osc.connect(gain);
    gain.connect(this.masterGain);
    overtone.connect(oGain);
    oGain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.28);
    overtone.start(now + 0.05);
    overtone.stop(now + 0.25);
  }

  playShieldConsume(): void {
    if (this.isMuted()) return;
    const now = this.ctx.currentTime;

    // Soft dissolve — descending tone with gentle noise fade, like a bubble popping softly
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(500, now);
    osc.frequency.exponentialRampToValueAtTime(250, now + 0.12);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

    const bufLen = Math.floor(this.ctx.sampleRate * 0.08);
    const buf = this.ctx.createBuffer(1, bufLen, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufLen * 0.3));
    }
    const noiseSrc = this.ctx.createBufferSource();
    noiseSrc.buffer = buf;

    const noiseFilter = this.ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 1500;
    noiseFilter.Q.value = 0.8;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0, now + 0.03);
    noiseGain.gain.linearRampToValueAtTime(0.08, now + 0.05);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

    osc.connect(gain);
    gain.connect(this.masterGain);
    noiseSrc.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.15);
    noiseSrc.start(now + 0.03);
    noiseSrc.stop(now + 0.14);
  }

  playStreakBreak(): void {
    if (this.isMuted()) return;
    const now = this.ctx.currentTime;

    // Gentle two-note descend — like a pebble dropping into water
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(250, now + 0.18);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.18);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 0.2);
  }

  playStreakTierUp(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Bright ascending bloom — two harmonized notes rising, celebratory but gentle
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(523, now);
    osc1.frequency.linearRampToValueAtTime(659, now + 0.1);

    const g1 = this.ctx.createGain();
    g1.gain.setValueAtTime(0, now);
    g1.gain.linearRampToValueAtTime(0.18, now + 0.02);
    g1.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(784, now + 0.08);
    osc2.frequency.linearRampToValueAtTime(988, now + 0.18);

    const g2 = this.ctx.createGain();
    g2.gain.setValueAtTime(0, now + 0.08);
    g2.gain.linearRampToValueAtTime(0.15, now + 0.1);
    g2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);

    osc1.connect(g1);
    g1.connect(this.masterGain);
    osc2.connect(g2);
    g2.connect(this.masterGain);

    osc1.start(now);
    osc1.stop(now + 0.22);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.28);
  }

  playLevelComplete(): void {
    if (this.isMuted()) return;
    this.duck();
    const now = this.ctx.currentTime;

    // Triumphant nature fanfare — rising major chord with shimmer, like sunlight breaking through
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const start = now + i * 0.12;

      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.2, start + 0.03);
      gain.gain.setValueAtTime(0.2, start + 0.15);
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.4);

      // Shimmering detuned layer
      const shimmer = this.ctx.createOscillator();
      shimmer.type = 'sine';
      shimmer.frequency.value = freq * 1.005;

      const sGain = this.ctx.createGain();
      sGain.gain.setValueAtTime(0, start);
      sGain.gain.linearRampToValueAtTime(0.08, start + 0.05);
      sGain.gain.exponentialRampToValueAtTime(0.01, start + 0.35);

      osc.connect(gain);
      gain.connect(this.masterGain);
      shimmer.connect(sGain);
      sGain.connect(this.masterGain);

      osc.start(start);
      osc.stop(start + 0.45);
      shimmer.start(start);
      shimmer.stop(start + 0.4);
    });
  }

}

let instance: SoundSystem | null = null;

export function getSoundSystem(): SoundSystem {
  if (!instance) {
    instance = new SoundSystem();
  }
  return instance;
}
